import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  Checkbox,
  Chip,
  FormControl,
  IconButton,
  MenuItem,
  Select,
  TableBody,
  TableCell,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CheckOutlined from '@mui/icons-material/CheckOutlined';
import DeleteOutline from '@mui/icons-material/DeleteOutline';
import EditOutlined from '@mui/icons-material/EditOutlined';
import { DAYS_OF_WEEK } from '../../../../app/services/api/range';
import { TableSection } from '../../../../components/shared/tables/TableSection';
import type { TableColumn } from '../../../../components/shared/tables/TableSection';
import { ServicePeriodFormValue } from './ServicePeriodDialog';

const DAY_LABEL_KEYS = ['daySun', 'dayMon', 'dayTue', 'dayWed', 'dayThu', 'dayFri', 'daySat'];
const SELECT_ALL_DAYS = -1;
const DISPLAY_DAYS_OF_WEEK = [1, 2, 3, 4, 5, 6, 0];
const PAGE_SIZE = 10;

interface MenuServicePeriodsTabProps {
  servicePeriods: ServicePeriodFormValue[];
  onChange: (servicePeriods: ServicePeriodFormValue[]) => void;
  readOnly?: boolean;
}

const createLocalId = (): string => `sp-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

const MenuServicePeriodsTab = ({ servicePeriods, onChange, readOnly = false }: MenuServicePeriodsTabProps) => {
  const { t } = useTranslation('range');
  const [editingRowIds, setEditingRowIds] = useState<Set<string>>(new Set());
  const [page, setPage] = useState(0);

  useEffect(() => {
    if (editingRowIds.size === 0) return undefined;

    const handlePointerDown = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      const row = target.closest<HTMLElement>('[data-service-period-row]');
      const isDayOption = target.closest('[role="listbox"], [role="option"]');

      if (!row && !isDayOption) {
        setEditingRowIds((current) => new Set(
          servicePeriods
            .filter((period) => current.has(period.localId) && !period.name.trim())
            .map((period) => period.localId),
        ));
      }
    };

    document.addEventListener('mousedown', handlePointerDown);
    return () => document.removeEventListener('mousedown', handlePointerDown);
  }, [editingRowIds, servicePeriods]);

  const updateRow = (localId: string, changes: Partial<ServicePeriodFormValue>) => {
    onChange(servicePeriods.map((period) => (
      period.localId === localId ? { ...period, ...changes } : period
    )));
  };

  const setEditing = (localId: string, editing: boolean) => {
    setEditingRowIds((current) => {
      const next = new Set(current);
      if (editing) next.add(localId);
      else next.delete(localId);
      return next;
    });
  };

  const handleAdd = () => {
    if (readOnly) return;
    const localId = createLocalId();
    const nextServicePeriods = [
      ...servicePeriods,
      { localId, name: '', startTime: '09:00', endTime: '17:00', daysOfWeek: [...DAYS_OF_WEEK] },
    ];
    setEditing(localId, true);
    setPage(Math.max(Math.ceil(nextServicePeriods.length / PAGE_SIZE) - 1, 0));
    onChange(nextServicePeriods);
  };

  const handleRemove = (localId: string) => {
    if (readOnly) return;
    setEditing(localId, false);
    onChange(servicePeriods.filter((period) => period.localId !== localId));
  };

  const renderDayChips = (days: number[]) => (
    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
      {days.length > 0 ? DISPLAY_DAYS_OF_WEEK.filter((day) => days.includes(day)).map((day) => (
        <Chip key={day}
          label={t(DAY_LABEL_KEYS[day])}
          size="small" />
      )) : null}
    </Box>
  );

  const maxPages = Math.max(Math.ceil(servicePeriods.length / PAGE_SIZE), 1);
  const currentPage = Math.min(page, maxPages - 1);
  const pageRows = servicePeriods.slice(currentPage * PAGE_SIZE, (currentPage + 1) * PAGE_SIZE);

  const columns: TableColumn<ServicePeriodFormValue>[] = [
    { id: 'name', label: t('name') },
    { id: 'startTime', label: t('startTime') },
    { id: 'endTime', label: t('endTime') },
    { id: 'days', label: t('days') },
    { id: 'actions', label: t('actions'), align: 'right' },
  ];

  return (
    <TableSection<ServicePeriodFormValue>
      title={t('servicePeriods')}
      columns={columns}
      rows={pageRows}
      getRowId={(period) => period.localId}
      recordCount={servicePeriods.length}
      recordCountLabel={t('servicePeriods').toLowerCase()}
      page={currentPage}
      maxPages={maxPages}
      onPageChange={setPage}
      actions={(
        <Button variant="secondary"
          startIcon={<AddIcon />}
          disabled={readOnly}
          onClick={handleAdd}>
          {t('addServicePeriod')}
        </Button>
      )}
      renderBody={(rows) => (
        <TableBody>
          {rows.map((period) => {
            const isEditing = editingRowIds.has(period.localId);
            return (
              <TableRow
                key={period.localId}
                data-service-period-row
              >
                <TableCell sx={{ minWidth: 180 }}>
                  {isEditing ? (
                    <TextField
                      value={period.name}
                      disabled={readOnly}
                      onChange={(event) => updateRow(period.localId, { name: event.target.value })}
                      placeholder={t('name')}
                      size="small"
                      fullWidth
                      required
                      error={!period.name.trim()}
                    />
                  ) : period.name}
                </TableCell>
                <TableCell sx={{ minWidth: 130 }}>
                  {isEditing ? (
                    <TextField
                      type="time"
                      value={period.startTime}
                      disabled={readOnly}
                      onChange={(event) => updateRow(period.localId, { startTime: event.target.value })}
                      size="small"
                      fullWidth
                      InputLabelProps={{ shrink: true }}
                    />
                  ) : period.startTime}
                </TableCell>
                <TableCell sx={{ minWidth: 130 }}>
                  {isEditing ? (
                    <TextField
                      type="time"
                      value={period.endTime}
                      disabled={readOnly}
                      onChange={(event) => updateRow(period.localId, { endTime: event.target.value })}
                      size="small"
                      fullWidth
                      InputLabelProps={{ shrink: true }}
                    />
                  ) : period.endTime}
                </TableCell>
                <TableCell sx={{ minWidth: 300 }}>
                  {isEditing ? (
                    <FormControl fullWidth
                      size="small">
                      <Select
                        multiple
                        value={period.daysOfWeek}
                        disabled={readOnly}
                        displayEmpty
                        MenuProps={{ PaperProps: { sx: { maxHeight: 280 } } }}
                        onChange={(event) => {
                          const value = event.target.value;
                          const selectedDays = typeof value === 'string' ? value.split(',').map(Number) : value;
                          const days = selectedDays.includes(SELECT_ALL_DAYS)
                            ? (period.daysOfWeek.length === DAYS_OF_WEEK.length ? [] : [...DAYS_OF_WEEK])
                            : selectedDays.sort((a, b) => a - b);
                          updateRow(period.localId, { daysOfWeek: days });
                        }}
                        renderValue={(selected) => (
                          (selected as number[]).length > 0
                            ? renderDayChips(selected as number[])
                            : <Typography variant="textSM"
                              color="text.secondary">{t('selectDays')}</Typography>
                        )}
                      >
                        <MenuItem value={SELECT_ALL_DAYS}>
                          <Checkbox checked={period.daysOfWeek.length === DAYS_OF_WEEK.length} />
                          {t('selectAllDays')}
                        </MenuItem>
                        {DISPLAY_DAYS_OF_WEEK.map((day) => (
                          <MenuItem key={day}
                            value={day}>
                            <Checkbox checked={period.daysOfWeek.includes(day)} />
                            {t(DAY_LABEL_KEYS[day])}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  ) : renderDayChips(period.daysOfWeek)}
                </TableCell>
                <TableCell align="right">
                  <Tooltip title={isEditing ? t('done') : t('edit')}>
                    <IconButton
                      size="small"
                      disabled={readOnly}
                      onClick={() => {
                        if (isEditing && !period.name.trim()) return;
                        setEditing(period.localId, !isEditing);
                      }}
                      aria-label={isEditing ? t('done') : t('edit')}
                    >
                      {isEditing ? <CheckOutlined fontSize="small" /> : <EditOutlined fontSize="small" />}
                    </IconButton>
                  </Tooltip>
                  <Tooltip title={t('remove')}>
                    <IconButton
                      size="small"
                      disabled={readOnly}
                      onClick={() => handleRemove(period.localId)}
                      aria-label={t('remove')}
                    >
                      <DeleteOutline fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      )}
    />
  );
};

export default MenuServicePeriodsTab;
